import React from 'react'
import { Pressable, Text, View } from 'react-native'
import { useVideoPlayer, VideoView } from 'expo-video'
import { useTranslation } from 'react-i18next'

import { MAX_VIDEO_SIZE, VALID_VIDEO_TYPE } from '@/constants/common'
import { launchMediaLibraryAsync } from '@/utils/mediaPicker'
import { useToast } from '@/components/base'
import { Spinner, VideoCall } from '@/components/icon'

function UploadVideoButton({
  videoSource,
  setVideoSource,
  onChangeVideoUri
}: {
  videoSource: string
  setVideoSource: (src: string) => void
  onChangeVideoUri: (src: string) => void
}) {
  const { t } = useTranslation()
  const toast = useToast()
  async function pickVideo() {
    const result = await launchMediaLibraryAsync({
      mediaTypes: ['videos'],
      quality: 1
    })
    if (!result.canceled) {
      const { uri, fileName, fileSize, mimeType } = result.assets[0]
      const inferredType = getVideoType(fileName ?? uri)
      if (!VALID_VIDEO_TYPE.includes(mimeType ?? inferredType)) {
        toast.show({ variant: 'error', description: t('ui:invalidVideoFormat') })
        return
      }
      const measuredFileSize = fileSize ?? (await (await fetch(uri)).blob()).size
      if (measuredFileSize > MAX_VIDEO_SIZE) {
        toast.show({ variant: 'error', description: t('ui:videoTooLarge') })
        return
      }
      onChangeVideoUri(uri)
      setVideoSource(uri)
    }
  }

  return (
    <View className={'gap-1'}>
      <Text className={'text-sm font-medium text-text-secondary'}>{t('ui:uploadVideoProof')}</Text>
      <Pressable
        className={'h-20 items-center justify-center overflow-hidden rounded-lg bg-background-secondary'}
        onPress={pickVideo}
      >
        {videoSource === 'loading' ? (
          <View className={'animate-spin'}>
            <Spinner className={'size-6 text-primary'} />
          </View>
        ) : videoSource ? (
          <VideoPreview source={videoSource} />
        ) : (
          <VideoCall className={'size-6 text-text-quaternary'} />
        )}
      </Pressable>
      <Text className={'text-xs font-medium text-text-tertiary'}>
        {t('ui:videoUploadHint')}
      </Text>
    </View>
  )
}

function VideoPreview({ source }: { source: string }) {
  const player = useVideoPlayer(source)
  return (
    <VideoView style={{ width: '100%', height: '100%' }} player={player} nativeControls={false} contentFit={'cover'} />
  )
}

function getVideoType(source: string) {
  const extension = source.split('?')[0].split('.').pop()?.toLowerCase()
  if (extension === 'mp4') return 'video/mp4'
  if (extension === 'avi') return 'video/x-msvideo'
  if (extension === 'mov') return 'video/quicktime'
  return ''
}

export default UploadVideoButton
